/**
 * Turf catalog — `GET /geo/regions`, tenant-scoped from the bearer token.
 *
 * This module replaces `MAP_REGIONS`, a fourteen-entry object of Platte
 * County subregions that shipped inside the JS bundle and was therefore
 * offered to EVERY tenant. A second county signing up did not get an empty
 * turf picker, it got Parkville and Weston — wrong names on the scope editor,
 * wrong precinct lists written into `user_scopes`, and a county map rolled up
 * over precinct numbers that mean something else entirely. Turfs are tenant
 * data; they belong behind the token.
 *
 * ## The one conversion point
 *
 * The wire sends `ld` and every `precinct_num` as STRINGS (they are `String`
 * columns — precinct identifiers are not arithmetic, and some jurisdictions
 * use "3A"). `MAP_REGIONS` stored both as numbers, and eighteen consumers were
 * written against that. Rather than sprinkle `String()`/`parseInt()` through
 * all of them, `buildMapRegionCatalog` normalizes ONCE, here:
 *
 *   - `ld` → number, matching what the views compare and index with
 *     (`LD_COLORS[ld]`, `LD_NAMES[ld]`, `district_key` parsing).
 *   - `precincts` → numbers, matching `D.precincts[].precinct_num`.
 *   - `precinctsAsStrings` → the write shape, precomputed, because every
 *     scope-writing call site needs it and `kind: 'subregion'` 422s on ints
 *     (see the note on `UserScopeInput` in `src/types/index.ts`).
 *
 * A precinct id that is not a clean integer would `NaN` on the way through, so
 * it is dropped from `precincts` and kept in `precinctsAsStrings` — the write
 * path stays lossless even where the numeric rollup cannot follow.
 */
import client from '@/api/client';

/** Exactly what `GET /geo/regions` serves, before any coercion. */
export interface GeoRegionWire {
  key: string;
  name: string;
  ld: string;
  description: string | null;
  map_file: string | null;
  precinct_count: number;
  precincts: { precinct_num: string; ld: string }[];
}

/**
 * One turf, in the shape the views want.
 *
 * Deliberately close to the old `MAP_REGIONS` value shape (`name`, `ld`,
 * `precincts`, `description`, `mapFile`) so the consumer diffs stayed small
 * and reviewable. `key`, `precinctsAsStrings`, and `precinctCount` are the
 * additions: the key so a region can be passed around on its own without its
 * map entry, the string precincts so write paths never re-coerce, and the
 * server's own count so a UI can say "12 precincts" even when a precinct
 * number failed numeric coercion.
 */
export interface MapRegion {
  key: string;
  name: string;
  ld: number;
  /** Numeric precinct ids, for joining against voter-roll rollups. */
  precincts: number[];
  /**
   * The same ids as the wire sent them, de-duplicated — the shape scope
   * writes require. May be SHORTER than the wire's `precincts` array, which
   * lists (precinct, LD) memberships; see `buildMapRegionCatalog`.
   */
  precinctsAsStrings: string[];
  description: string;
  mapFile: string | null;
  precinctCount: number;
}

/**
 * The whole tenant's turfs, plus the two derived lists every picker rebuilt
 * by hand (`Object.keys(...).sort()` and the distinct-LD set).
 *
 * `keys` preserves the server's `display_order, key` ordering rather than
 * re-sorting: display order is a tenant-editable column precisely so a county
 * can put its turfs in the sequence its coordinators think in, and a
 * client-side `.sort()` would throw that away.
 */
export interface MapRegionCatalog {
  byKey: Record<string, MapRegion>;
  /** Turf keys in server display order. */
  keys: string[];
  /** Distinct LDs present, as strings, numerically ascending. */
  ldOptions: string[];
  /** True when this tenant has no turfs loaded. See `MapRegionsContext`. */
  isEmpty: boolean;
}

/**
 * The catalog a tenant has before anyone loads its turfs — and the value the
 * context holds while the fetch is in flight.
 *
 * Frozen because it is a shared singleton: a consumer that mutated it would
 * corrupt every other consumer's view of "no turfs". Note there is NO Platte
 * fallback anywhere in this module; empty is the only default.
 */
export const EMPTY_MAP_REGION_CATALOG: MapRegionCatalog = Object.freeze({
  byKey: Object.freeze({}) as Record<string, MapRegion>,
  keys: Object.freeze([]) as unknown as string[],
  ldOptions: Object.freeze([]) as unknown as string[],
  isEmpty: true,
});

/**
 * Normalize the wire payload into the catalog. Pure, and exported so tests can
 * build a fixture catalog without a server — the conversion rules are part of
 * what needs covering, so fixtures go through this rather than around it.
 */
export function buildMapRegionCatalog(rows: GeoRegionWire[]): MapRegionCatalog {
  const byKey: Record<string, MapRegion> = {};
  const keys: string[] = [];
  const lds = new Set<string>();

  for (const row of rows) {
    // De-duplicated, because `precincts` is a list of (precinct, LD)
    // MEMBERSHIPS, not of precincts: a precinct an LD line cuts through can
    // contribute two arms to the same turf, and the server says so explicitly
    // — `precinct_count` is the DISTINCT count and can be smaller than
    // `precincts.length`. Leaving the duplicate in would double-count that
    // precinct in every client-side rollup (the county map sums voter totals
    // over this list) and would send the same precinct twice on a scope write.
    // Insertion order is preserved so the first LD arm keeps its position.
    const precinctsAsStrings = Array.from(
      new Set((row.precincts ?? []).map((p) => String(p.precinct_num))),
    );
    byKey[row.key] = {
      key: row.key,
      name: row.name,
      ld: Number(row.ld),
      // Non-numeric precinct ids survive in `precinctsAsStrings` but cannot
      // join against the numeric voter-roll rollups, so they are dropped here
      // instead of poisoning a sum with NaN.
      precincts: precinctsAsStrings
        .map((p) => Number.parseInt(p, 10))
        .filter((n) => Number.isFinite(n)),
      precinctsAsStrings,
      description: row.description ?? '',
      mapFile: row.map_file ?? null,
      precinctCount: row.precinct_count ?? precinctsAsStrings.length,
    };
    keys.push(row.key);
    lds.add(String(row.ld));
  }

  return {
    byKey,
    keys,
    ldOptions: Array.from(lds).sort((a, b) => Number(a) - Number(b)),
    isEmpty: keys.length === 0,
  };
}

/**
 * Which turf a precinct belongs to.
 *
 * Lives here rather than in each view because the precinct id arrives in three
 * different forms depending on the caller — a number from a drill click, a
 * string from a DB row, a route param — and every view that re-implemented the
 * match got the coercion subtly different. Comparison is on strings, which is
 * the form the server actually stores.
 *
 * A precinct can appear in more than one turf when a precinct is split across
 * legislative districts (Platte's precinct 13 sits in three). First match in
 * display order wins, which is what the old `Object.entries(...).find()` did.
 */
export function regionForPrecinct(
  catalog: MapRegionCatalog,
  precinct: string | number | null | undefined,
): MapRegion | null {
  if (precinct === null || precinct === undefined || precinct === '') return null;
  const want = String(precinct);
  for (const key of catalog.keys) {
    if (catalog.byKey[key].precinctsAsStrings.includes(want)) {
      return catalog.byKey[key];
    }
  }
  return null;
}

/** Turfs inside one legislative district, in display order. */
export function regionsInLd(
  catalog: MapRegionCatalog,
  ld: string | number | null | undefined,
): MapRegion[] {
  if (ld === null || ld === undefined || ld === '') return [];
  const want = String(ld);
  return catalog.keys
    .map((k) => catalog.byKey[k])
    .filter((r) => String(r.ld) === want);
}

/**
 * Recover a turf key from a scope the server resolved.
 *
 * `/auth/me` and the admin APIs return scopes as `{kind, ld, precincts}` with
 * no turf key attached, so an editor rehydrating a saved scope has to match it
 * back. Matching is on the SET of precincts, not the sequence: the wire order
 * comes from whatever the server's join produced and is not the catalog's.
 */
export function regionKeyForScope(
  catalog: MapRegionCatalog,
  ld: string | number,
  precincts: readonly (string | number)[] | null | undefined,
): string | null {
  const want = [...(precincts ?? [])].map(String).sort().join(',');
  const wantLd = String(ld);
  for (const key of catalog.keys) {
    const region = catalog.byKey[key];
    if (String(region.ld) !== wantLd) continue;
    if ([...region.precinctsAsStrings].sort().join(',') === want) return key;
  }
  return null;
}

/**
 * Fetch this tenant's turfs. Any authenticated role may read them — turf
 * geometry is not sensitive, and a canvasser's breadcrumbs need the names.
 */
export async function fetchMapRegions(): Promise<MapRegionCatalog> {
  const res = await client.get<GeoRegionWire[]>('/geo/regions');
  return buildMapRegionCatalog(res.data ?? []);
}
